import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BatchImport() {
  const { user, loading: authLoading } = useAuth();
  const { companyId, selectedCompany } = useCompany();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(droppedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    
    if (!companyId) {
      toast.error('Please select a company first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/tickets/batch-import?company_id=${companyId}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      
      if (response.ok) {
        setResult(data);
        if (data.imported > 0) {
          toast.success(`Successfully imported ${data.imported} tickets`);
        }
        if (data.errors?.length > 0) {
          toast.warning(`${data.errors.length} rows had errors`);
        }
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Import failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'batch_number,product_name,quantity,unit,production_date\nBATCH-001,Widget A,100,kg,2024-01-15\nBATCH-002,Widget B,50,pcs,2024-01-15';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ticket_import_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (authLoading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="batch-import-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/tickets')}
            className="h-10 w-10 p-0"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Batch Import</h1>
            <p className="text-slate-500 mt-1">Import multiple production entries from CSV</p>
          </div>
        </div>

        <div className="max-w-2xl space-y-6">
          {/* Instructions */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">CSV Format Requirements</h3>
            <p className="text-sm text-slate-600 mb-4">
              Your CSV file should have the following columns:
            </p>
            <div className="bg-white border border-slate-200 rounded-md p-4 font-mono text-sm text-slate-700 mb-4 overflow-x-auto">
              batch_number, product_name, quantity, unit, production_date
            </div>
            <Button
              variant="outline"
              onClick={downloadTemplate}
              className="h-10"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </div>

          {/* File Upload */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-slate-900 bg-slate-50'
                  : file
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-300 hover:border-slate-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="drop-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-input"
              />
              
              {file ? (
                <div className="flex flex-col items-center">
                  <FileText className="w-12 h-12 text-green-600 mb-3" />
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFile(null);
                      setResult(null);
                    }}
                    className="mt-4 h-10"
                    data-testid="remove-file-btn"
                  >
                    Remove File
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="w-12 h-12 text-slate-400 mb-3" />
                  <p className="font-medium text-slate-700">
                    Drag and drop your CSV file here
                  </p>
                  <p className="text-sm text-slate-500 mt-1">or</p>
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-3 h-10"
                    data-testid="browse-files-btn"
                  >
                    Browse Files
                  </Button>
                </div>
              )}
            </div>

            {file && !result && (
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="h-12 px-8 bg-slate-900 hover:bg-slate-800"
                  data-testid="upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Importing...' : 'Import Tickets'}
                </Button>
              </div>
            )}
          </div>

          {/* Results */}
          {result && (
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="import-result">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Import Results</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-700">Imported</span>
                  </div>
                  <p className="text-2xl font-bold text-green-700">{result.imported}</p>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-700">Errors</span>
                  </div>
                  <p className="text-2xl font-bold text-red-700">{result.errors?.length || 0}</p>
                </div>
              </div>

              {result.errors?.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700">Error Details</h4>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {result.errors.map((error, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 text-sm text-red-600 border-b border-slate-100 last:border-b-0"
                      >
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex items-center gap-4">
                <Button
                  onClick={() => navigate('/tickets')}
                  className="h-12 px-6 bg-slate-900 hover:bg-slate-800"
                  data-testid="view-tickets-btn"
                >
                  View All Tickets
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFile(null);
                    setResult(null);
                  }}
                  className="h-12"
                  data-testid="import-another-btn"
                >
                  Import Another File
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
